import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import {
  CheckCircle,
  XCircle,
  Clock,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../../../../components/ui/avatar';
import { Badge } from '../../../../../components/ui/badge';
import reviewConstants from '../../../../../constants/review.constants';
import { BigHead } from '../../../../../lib/core.cjs.development';
import { cn } from '../../../../../lib/utils';
import ReviewProgress from './ReviewProgress';

function getStatusBadge(status, hasRevertedIds) {
  const statusConfig = {
    [reviewConstants.REVIEW_STATUSES.CONFIRMED]: {
      icon: CheckCircle,
      label: 'Confirmed',
      variant: 'default',
      className: 'bg-green-500/10 text-green-600 border-green-500/20',
    },
    [reviewConstants.REVIEW_STATUSES.DECLINED]: {
      icon: XCircle,
      label: 'Declined',
      variant: 'destructive',
      className: 'bg-red-500/10 text-red-600 border-red-500/20',
    },
    [reviewConstants.REVIEW_STATUSES.PROCESSED]: {
      icon: Clock,
      label: 'Processed',
      variant: 'secondary',
      className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    },
    [reviewConstants.REVIEW_STATUSES.REVERTED]: {
      icon: RotateCcw,
      label: 'Reverted',
      variant: 'outline',
      className: 'bg-muted text-muted-foreground',
    },
  };

  const config = statusConfig[status] || statusConfig[reviewConstants.REVIEW_STATUSES.PROCESSED];
  const Icon = config.icon;

  return (
    <div className="flex items-center gap-1">
      <Badge variant="outline" className={cn('gap-1 font-normal', config.className)}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
      {hasRevertedIds && status !== reviewConstants.REVIEW_STATUSES.REVERTED && (
        <RotateCcw className="h-3 w-3 text-muted-foreground" />
      )}
    </div>
  );
}

function getInitials(email) {
  if (!email) return '?';
  const parts = email.split('@')[0].split('.');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return email.substring(0, 2).toUpperCase();
}

function normalizeRecordLinks(reviewRecordLinks) {
  if (Array.isArray(reviewRecordLinks)) {
    return reviewRecordLinks;
  }
  if (reviewRecordLinks) {
    return [reviewRecordLinks];
  }
  return [];
}

function ReviewsTableRow({ review, allReviews }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    _id,
    email,
    avatar,
    department,
    reviewGoal,
    newGoal,
    status,
    confirmed,
    declined,
    progress,
    updatedAt,
    revertedIds,
    reviewRecordLinks,
  } = review;

  const reviewDate = format(new Date(updatedAt), 'MMM d, yyyy');

  // Get reverted reviews for this row
  const revertedReviews = revertedIds ? allReviews.filter((r) => revertedIds.includes(r._id)) : [];

  const hasRevertedRows =
    revertedIds && revertedIds.length > 0 && status !== reviewConstants.REVIEW_STATUSES.REVERTED;

  // Normalize review record links to array
  const recordLinks = normalizeRecordLinks(reviewRecordLinks);

  return (
    <>
      <tr className="border-b border-border hover:bg-muted/50 transition-colors">
        {/* Avatar */}
        <td className="px-3 py-3">
          <Avatar className="h-8 w-8">
            {avatar ? (
              <div className="h-8 w-8">
                <BigHead {...avatar} />
              </div>
            ) : (
              <>
                <AvatarImage src="" alt={email} />
                <AvatarFallback className="text-xs">{getInitials(email)}</AvatarFallback>
              </>
            )}
          </Avatar>
        </td>

        {/* Email */}
        <td className="px-3 py-3 text-sm truncate max-w-[180px]" title={email}>
          {email}
        </td>

        {/* Department */}
        <td className="px-3 py-3 text-sm">{department}</td>

        {/* Review Goal */}
        <td className="px-3 py-3 text-sm font-medium">{reviewGoal}</td>

        {/* Record Links */}
        <td className="px-3 py-3 text-sm">
          {recordLinks.length > 0 ? (
            <div className="flex flex-col gap-1">
              {recordLinks.map((link) => (
                <a
                  key={`${_id}-record-${link}`}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline text-xs"
                >
                  Record
                  <ExternalLink className="h-3 w-3" />
                </a>
              ))}
            </div>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </td>

        {/* New Goal */}
        <td className="px-3 py-3 text-sm font-medium">{newGoal || '-'}</td>

        {/* Status */}
        <td className="px-3 py-3 text-center">{getStatusBadge(status, hasRevertedRows)}</td>

        {/* Confirmed */}
        <td className="px-3 py-3 text-sm text-center">
          <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded bg-green-500/10 text-green-600 font-medium">
            {confirmed}
          </span>
        </td>

        {/* Declined */}
        <td className="px-3 py-3 text-sm text-center">
          <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded bg-red-500/10 text-red-600 font-medium">
            {declined}
          </span>
        </td>

        {/* Progress */}
        <td className="px-3 py-3">
          <ReviewProgress value={progress} />
        </td>

        {/* Review Date */}
        <td className="px-3 py-3 text-sm text-muted-foreground">{reviewDate}</td>

        {/* Expand Button */}
        <td className="px-3 py-3 text-center">
          {hasRevertedRows && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 rounded hover:bg-muted transition-colors"
              aria-label={isExpanded ? 'Collapse reverted reviews' : 'Expand reverted reviews'}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          )}
        </td>
      </tr>

      {/* Expanded Reverted Rows */}
      {isExpanded &&
        revertedReviews.length > 0 &&
        revertedReviews.map((revertedReview) => {
          const revertedLinks = normalizeRecordLinks(revertedReview.reviewRecordLinks);
          return (
            <tr key={revertedReview._id} className="bg-muted/30 border-b border-border/50">
              {/* Avatar */}
              <td className="px-3 py-2">
                <Avatar className="h-7 w-7">
                  {revertedReview.avatar ? (
                    <div className="h-7 w-7">
                      <BigHead {...revertedReview.avatar} />
                    </div>
                  ) : (
                    <>
                      <AvatarImage src="" alt={revertedReview.email} />
                      <AvatarFallback className="text-xs">
                        {getInitials(revertedReview.email)}
                      </AvatarFallback>
                    </>
                  )}
                </Avatar>
              </td>

              {/* Email */}
              <td className="px-3 py-2 text-sm text-muted-foreground truncate max-w-[180px]">
                {revertedReview.email}
              </td>

              {/* Department */}
              <td className="px-3 py-2 text-sm text-muted-foreground">
                {revertedReview.department}
              </td>

              {/* Review Goal */}
              <td className="px-3 py-2 text-sm text-muted-foreground">
                {revertedReview.reviewGoal}
              </td>

              {/* Record Links */}
              <td className="px-3 py-2 text-sm">
                {revertedLinks.length > 0 ? (
                  <div className="flex flex-col gap-1">
                    {revertedLinks.map((link) => (
                      <a
                        key={`${revertedReview._id}-record-${link}`}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary/70 hover:underline text-xs"
                      >
                        Record
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </td>

              {/* New Goal */}
              <td className="px-3 py-2 text-sm text-muted-foreground">
                {revertedReview.newGoal || '-'}
              </td>

              {/* Status */}
              <td className="px-3 py-2 text-center">
                <Badge
                  variant="outline"
                  className="gap-1 font-normal bg-muted text-muted-foreground"
                >
                  <RotateCcw className="h-3 w-3" />
                  Reverted
                </Badge>
              </td>

              {/* Confirmed */}
              <td className="px-3 py-2 text-sm text-center text-muted-foreground">
                {revertedReview.confirmed}
              </td>

              {/* Declined */}
              <td className="px-3 py-2 text-sm text-center text-muted-foreground">
                {revertedReview.declined}
              </td>

              {/* Progress */}
              <td className="px-3 py-2">
                <ReviewProgress value={revertedReview.progress} disabled />
              </td>

              {/* Review Date */}
              <td className="px-3 py-2 text-sm text-muted-foreground">
                {format(new Date(revertedReview.updatedAt), 'MMM d, yyyy')}
              </td>

              {/* Empty cell for expand column */}
              <td className="px-3 py-2" />
            </tr>
          );
        })}
    </>
  );
}

ReviewsTableRow.propTypes = {
  review: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    avatar: PropTypes.shape({}),
    department: PropTypes.string.isRequired,
    reviewGoal: PropTypes.string.isRequired,
    newGoal: PropTypes.string,
    status: PropTypes.string.isRequired,
    confirmed: PropTypes.number.isRequired,
    declined: PropTypes.number.isRequired,
    progress: PropTypes.number.isRequired,
    updatedAt: PropTypes.string.isRequired,
    revertedIds: PropTypes.arrayOf(PropTypes.string),
    reviewRecordLinks: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  }).isRequired,
  allReviews: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default ReviewsTableRow;
